import { createSlice } from '@reduxjs/toolkit';
import _ from 'lodash';

// Import legacy action types for extraReducers bridge
import {
  SELECT_RISK_SOURCE,
  DESELECT_RISK_SOURCE,
  TOGGLE_RISK_SOURCE,
  REGISTER_RISK_SOURCES,
  SET_RISK_SOURCE_PROGRESS,
  SET_RISK_SOURCE,
  SET_ROOT_CAUSE,
  SET_DEEP_DIVE_VALUES,
  SET_DEEP_DIVE_DETAIL,
  SAVE_RISK_SOURCE_START,
  SAVE_RISK_SOURCE_SUCCESS,
  SAVE_RISK_SOURCE_FAILURE,
  SAVE_CAUSES_START,
  SAVE_CAUSES_SUCCESS,
  SAVE_CAUSES_FAILURE,
  FETCH_RISK_CARD_DATA_START,
  FETCH_RISK_CARD_DATA_SUCCESS,
  FETCH_RISK_CARD_DATA_FAILURE,
} from '../actions/actionTypes.js';

// ============================================================================
// Risk Sources Slice
// Manages risk source selection, causes, and cause analysis for assessments
// ============================================================================

/**
 * State Shape:
 * {
 *   byAssessment: {
 *     [assessmentUuid]: {
 *       [sectionName]: {
 *         riskSources: Source[],
 *         selectedSources: Source[]
 *       }
 *     }
 *   },
 *   saving: boolean,
 *   savingCauses: boolean,
 *   loading: boolean,
 *   error: string | null,
 *   saveRiskSourceError: string | null,
 *   saveCausesError: string | null
 * }
 *
 * Source object shape:
 * {
 *   name: string,
 *   value: any[],
 *   selected: boolean,
 *   progress: number,
 *   causes: object,
 *   causesTemplate: object,
 *   causesByValue: { [valueKey]: causes }
 * }
 */

const initialState = {
  byAssessment: {},
  saving: false,
  savingCauses: false,
  loading: false,
  error: null,
  saveRiskSourceError: null,
  saveCausesError: null,
};

// Helper to ensure nested structure exists
const ensureStructure = (state, assessmentUuid, sectionName) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = {};
  }
  if (!state.byAssessment[assessmentUuid][sectionName]) {
    state.byAssessment[assessmentUuid][sectionName] = {
      riskSources: [],
      selectedSources: [],
    };
  }
};

const riskSourcesSlice = createSlice({
  name: 'riskSources',
  initialState,
  reducers: {
    // RTK action creators for new code

    selectRiskSource: (state, action) => {
      const { assessmentUuid, riskArea, src } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea);

      const section = state.byAssessment[assessmentUuid][riskArea];
      const already = section.selectedSources.some((s) => s.name === src.name);
      if (!already) {
        section.selectedSources.push(src);
      }
    },

    deselectRiskSource: (state, action) => {
      const { assessmentUuid, riskArea, srcName } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section) return;

      section.selectedSources = section.selectedSources.filter(
        (s) => s.name !== srcName
      );
    },

    toggleRiskSource: (state, action) => {
      const { assessmentUuid, riskArea, srcName } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section?.riskSources) return;

      section.riskSources = section.riskSources.map((s) =>
        s.name === srcName ? { ...s, selected: !s.selected } : s
      );
    },

    registerRiskSources: (state, action) => {
      const { assessmentUuid, riskArea, names } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea);

      const fresh = names.map((n) => ({ name: n, selected: false, progress: 0 }));
      state.byAssessment[assessmentUuid][riskArea].riskSources = fresh;
    },

    setRiskSourceProgress: (state, action) => {
      const { assessmentUuid, riskArea, srcName, progress } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section?.riskSources) return;

      section.riskSources = section.riskSources.map((s) =>
        s.name === srcName ? { ...s, progress } : s
      );
    },

    setRiskSource: (state, action) => {
      const { assessmentUuid, section, source, values } = action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((s) =>
        s.name === source ? { ...s, value: values } : s
      );
    },

    setRootCause: (state, action) => {
      const { assessmentUuid, section, source, valueKey, path, newValue } = action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);

        // Ensure causesTemplate exists
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        // Determine target blob
        let targetBlob;
        if (valueKey == null) {
          if (!clone.causes) {
            clone.causes = _.cloneDeep(clone.causesTemplate);
          }
          targetBlob = clone.causes;
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          targetBlob = clone.causesByValue[valueKey];
        }

        // Traverse and set value
        let node = targetBlob;
        for (let i = 0; i < path.length - 1; i++) {
          if (!node[path[i]]) node[path[i]] = {};
          node = node[path[i]];
        }
        const lastKey = path[path.length - 1];
        const existing = node[lastKey];
        node[lastKey] =
          existing && typeof existing === 'object'
            ? { ...existing, value: newValue }
            : { value: newValue };

        return clone;
      });
    },

    setDeepDiveValues: (state, action) => {
      const { assessmentUuid, section, source, valueKey, causeCategory, deepValues } =
        action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        let bucket;
        if (valueKey == null) {
          if (!clone.causes) clone.causes = _.cloneDeep(clone.causesTemplate);
          if (!clone.causes[causeCategory]) clone.causes[causeCategory] = {};
          bucket = clone.causes[causeCategory];
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          if (!clone.causesByValue[valueKey][causeCategory]) {
            clone.causesByValue[valueKey][causeCategory] = {};
          }
          bucket = clone.causesByValue[valueKey][causeCategory];
        }
        bucket.deepValues = deepValues;

        return clone;
      });
    },

    setDeepDiveDetail: (state, action) => {
      const { assessmentUuid, section, source, valueKey, causeCategory, option, detail } =
        action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        let bucket;
        if (valueKey == null) {
          if (!clone.causes) clone.causes = _.cloneDeep(clone.causesTemplate);
          if (!clone.causes[causeCategory]) clone.causes[causeCategory] = {};
          bucket = clone.causes[causeCategory];
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          if (!clone.causesByValue[valueKey][causeCategory]) {
            clone.causesByValue[valueKey][causeCategory] = {};
          }
          bucket = clone.causesByValue[valueKey][causeCategory];
        }
        bucket.deepDetails = bucket.deepDetails || {};
        bucket.deepDetails[option] = detail;

        return clone;
      });
    },

    // Clear all sources for an assessment
    clearAssessmentSources: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },

    // Set loading/error states
    setSaving: (state, action) => {
      state.saving = action.payload;
    },
    setSavingCauses: (state, action) => {
      state.savingCauses = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },

  extraReducers: (builder) => {
    // ============================================================================
    // Legacy Action Type Bridge
    // ============================================================================

    builder.addCase(SELECT_RISK_SOURCE, (state, action) => {
      const { assessmentUuid, riskArea, src } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea);

      const section = state.byAssessment[assessmentUuid][riskArea];
      const already = section.selectedSources.some((s) => s.name === src.name);
      if (!already) {
        section.selectedSources.push(src);
      }
    });

    builder.addCase(DESELECT_RISK_SOURCE, (state, action) => {
      const { assessmentUuid, riskArea, srcName } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section) return;

      section.selectedSources = section.selectedSources.filter(
        (s) => s.name !== srcName
      );
    });

    builder.addCase(TOGGLE_RISK_SOURCE, (state, action) => {
      const { assessmentUuid, riskArea, srcName } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section?.riskSources) return;

      section.riskSources = section.riskSources.map((s) =>
        s.name === srcName ? { ...s, selected: !s.selected } : s
      );
    });

    builder.addCase(REGISTER_RISK_SOURCES, (state, action) => {
      const { assessmentUuid, riskArea, names } = action.payload;
      ensureStructure(state, assessmentUuid, riskArea);

      const fresh = names.map((n) => ({ name: n, selected: false, progress: 0 }));
      state.byAssessment[assessmentUuid][riskArea].riskSources = fresh;
    });

    builder.addCase(SET_RISK_SOURCE_PROGRESS, (state, action) => {
      const { assessmentUuid, riskArea, srcName, progress } = action.payload;
      const section = state.byAssessment[assessmentUuid]?.[riskArea];
      if (!section?.riskSources) return;

      section.riskSources = section.riskSources.map((s) =>
        s.name === srcName ? { ...s, progress } : s
      );
    });

    builder.addCase(SET_RISK_SOURCE, (state, action) => {
      const { assessmentUuid, section, source, values } = action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((s) =>
        s.name === source ? { ...s, value: values } : s
      );
    });

    builder.addCase(SET_ROOT_CAUSE, (state, action) => {
      const { assessmentUuid, section, source, valueKey, path, newValue } = action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        let targetBlob;
        if (valueKey == null) {
          if (!clone.causes) clone.causes = _.cloneDeep(clone.causesTemplate);
          targetBlob = clone.causes;
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          targetBlob = clone.causesByValue[valueKey];
        }

        let node = targetBlob;
        for (let i = 0; i < path.length - 1; i++) {
          if (!node[path[i]]) node[path[i]] = {};
          node = node[path[i]];
        }
        const lastKey = path[path.length - 1];
        const existing = node[lastKey];
        node[lastKey] =
          existing && typeof existing === 'object'
            ? { ...existing, value: newValue }
            : { value: newValue };

        return clone;
      });
    });

    builder.addCase(SET_DEEP_DIVE_VALUES, (state, action) => {
      const { assessmentUuid, section, source, valueKey, causeCategory, deepValues } =
        action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        let bucket;
        if (valueKey == null) {
          if (!clone.causes) clone.causes = _.cloneDeep(clone.causesTemplate);
          if (!clone.causes[causeCategory]) clone.causes[causeCategory] = {};
          bucket = clone.causes[causeCategory];
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          if (!clone.causesByValue[valueKey][causeCategory]) {
            clone.causesByValue[valueKey][causeCategory] = {};
          }
          bucket = clone.causesByValue[valueKey][causeCategory];
        }
        bucket.deepValues = deepValues;

        return clone;
      });
    });

    builder.addCase(SET_DEEP_DIVE_DETAIL, (state, action) => {
      const { assessmentUuid, section, source, valueKey, causeCategory, option, detail } =
        action.payload;
      const sectionData = state.byAssessment[assessmentUuid]?.[section];
      if (!sectionData?.riskSources) return;

      sectionData.riskSources = sectionData.riskSources.map((src) => {
        if (src.name !== source) return src;

        const clone = _.cloneDeep(src);
        if (!clone.causesTemplate) {
          clone.causesTemplate = _.cloneDeep(clone.causes || {});
        }

        let bucket;
        if (valueKey == null) {
          if (!clone.causes) clone.causes = _.cloneDeep(clone.causesTemplate);
          if (!clone.causes[causeCategory]) clone.causes[causeCategory] = {};
          bucket = clone.causes[causeCategory];
        } else {
          clone.causesByValue = clone.causesByValue || {};
          if (!clone.causesByValue[valueKey]) {
            clone.causesByValue[valueKey] = _.cloneDeep(clone.causesTemplate);
          }
          if (!clone.causesByValue[valueKey][causeCategory]) {
            clone.causesByValue[valueKey][causeCategory] = {};
          }
          bucket = clone.causesByValue[valueKey][causeCategory];
        }
        bucket.deepDetails = bucket.deepDetails || {};
        bucket.deepDetails[option] = detail;

        return clone;
      });
    });

    // Save risk source async states
    builder
      .addCase(SAVE_RISK_SOURCE_START, (state) => {
        state.saving = true;
        state.saveRiskSourceError = null;
      })
      .addCase(SAVE_RISK_SOURCE_SUCCESS, (state) => {
        state.saving = false;
      })
      .addCase(SAVE_RISK_SOURCE_FAILURE, (state, action) => {
        state.saving = false;
        state.saveRiskSourceError = action.payload;
      });

    // Save causes async states
    builder
      .addCase(SAVE_CAUSES_START, (state) => {
        state.savingCauses = true;
        state.saveCausesError = null;
      })
      .addCase(SAVE_CAUSES_SUCCESS, (state) => {
        state.savingCauses = false;
      })
      .addCase(SAVE_CAUSES_FAILURE, (state, action) => {
        state.savingCauses = false;
        state.saveCausesError = action.payload;
      });

    // Fetch risk card data
    builder
      .addCase(FETCH_RISK_CARD_DATA_START, (state, action) => {
        const { assessmentUuid } = action.payload;
        ensureStructure(state, assessmentUuid, '_loading');
        state.loading = true;
      })
      .addCase(FETCH_RISK_CARD_DATA_SUCCESS, (state, action) => {
        const { assessmentUuid, fetchedData } = action.payload;
        state.loading = false;

        // Helper to coerce value to array
        const toArray = (v) => (v == null ? [] : Array.isArray(v) ? v : [v]);

        Object.entries(fetchedData).forEach(([riskArea, riskSources]) => {
          ensureStructure(state, assessmentUuid, riskArea);
          const section = state.byAssessment[assessmentUuid][riskArea];

          // Build selectedSet for this area
          const selectedSet = new Set(section.selectedSources?.map((s) => s.name));

          riskSources.forEach(({ name, value, causes, causesByValue, selected }) => {
            // Find existing source or create new
            let src = section.riskSources.find((s) => s.name === name);
            if (!src) {
              src = { name, selected: false, progress: 0 };
              section.riskSources.push(src);
            }

            // Store original template before overwriting
            const originalTemplate = _.cloneDeep(src.causes || {});
            src.causesTemplate = originalTemplate;

            src.value = toArray(value);
            src.causesByValue = causesByValue || {};

            // Merge saved data into template
            if (causes && typeof causes === 'object') {
              src.causes = _.merge(_.cloneDeep(src.causesTemplate), _.cloneDeep(causes));
            } else {
              src.causes = _.cloneDeep(src.causesTemplate);
            }

            if (selected) selectedSet.add(name);
          });

          section.selectedSources = section.riskSources.filter((s) =>
            selectedSet.has(s.name)
          );
        });
      })
      .addCase(FETCH_RISK_CARD_DATA_FAILURE, (state, action) => {
        state.loading = false;
        state.error = action.payload?.error;
      });
  },
});

export const {
  selectRiskSource,
  deselectRiskSource,
  toggleRiskSource,
  registerRiskSources,
  setRiskSourceProgress,
  setRiskSource,
  setRootCause,
  setDeepDiveValues,
  setDeepDiveDetail,
  clearAssessmentSources,
  setSaving,
  setSavingCauses,
  setLoading,
  setError,
} = riskSourcesSlice.actions;

// ============================================================================
// Selectors
// ============================================================================

export const selectRiskSourcesByAssessment = (state) => state.riskSources.byAssessment;

export const selectRiskSourcesForSection = (state, assessmentUuid, sectionName) =>
  state.riskSources.byAssessment[assessmentUuid]?.[sectionName]?.riskSources || [];

export const selectSelectedSourcesForSection = (state, assessmentUuid, sectionName) =>
  state.riskSources.byAssessment[assessmentUuid]?.[sectionName]?.selectedSources || [];

export const selectRiskSourceByName = (state, assessmentUuid, sectionName, sourceName) =>
  state.riskSources.byAssessment[assessmentUuid]?.[sectionName]?.riskSources?.find(
    (s) => s.name === sourceName
  );

export const selectRiskSourcesSaving = (state) => state.riskSources.saving;
export const selectRiskSourcesSavingCauses = (state) => state.riskSources.savingCauses;
export const selectRiskSourcesLoading = (state) => state.riskSources.loading;
export const selectRiskSourcesError = (state) => state.riskSources.error;

/**
 * Get all selected sources across all sections for an assessment
 */
export const selectAllSelectedSources = (state, assessmentUuid) => {
  const sections = state.riskSources.byAssessment[assessmentUuid] || {};
  const allSelected = [];

  Object.keys(sections).forEach((sectionName) => {
    const sources = sections[sectionName]?.selectedSources || [];
    allSelected.push(...sources.map((s) => ({ ...s, sectionName })));
  });

  return allSelected;
};

/**
 * Count selected sources for an assessment
 */
export const selectSelectedSourcesCount = (state, assessmentUuid) =>
  selectAllSelectedSources(state, assessmentUuid).length;

export default riskSourcesSlice.reducer;
